import React, { FC, useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import {
	fromDateTimeLocalValue,
	toDateTimeLocalValue,
} from "@/features/training/lib/trainingDateUtils";
import {
	TrainingCourse,
	TrainingCourseDuration,
} from "@/features/training/model/Training/TrainingCourse";
import {
	TrainingSession,
	TrainingSessionRequest,
	TrainingSessionStatus,
} from "@/features/training/model/Training/TrainingSession";
import { EditCourseSessionValidation } from "@/features/training/pages/training-tab/panel-right/training-session/EditCourseSessionValidation";
import { CustomDeleteButton } from "@/features/training/ui";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomInput from "@/shared/forms/CustomInput";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	Autocomplete,
	Box,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Field, type FieldProps, Form, Formik } from "formik";

import { GroupEmployee } from "@/model/User/Employee";
import { UserSummary } from "@/model/User/UserSummary";

import { useFetchGroups } from "@/service/GroupQueries";
import { useFetchUserGroupMembershipStatus } from "@/service/GroupQueries";
import {
	useMutateDeleteTrainingSession,
	useMutateUpdateTrainingSession,
} from "@/service/TrainingMutations";
import { useFetchUserSummariesByCurrentCompany } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface EditCourseSessionProps {
	handleClose: () => void;
	open: boolean;
	session: TrainingSession;
	course: TrainingCourse;
}

type FormValues = {
	CourseId: number;
	Information: string;
	SessionDate: string;
	ExpiresDate: string | null;
	DisableExpiresDate: boolean;
	TrainerId: number | string;
	StudentIds: number[];
	GroupId: number | null | string;
	AddedGroupIds: number[];
	Duration: string;
};

type SessionWithOptionalGroup = TrainingSession & {
	GroupId?: number | null;
	Group?: { Id?: number | null } | null;
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
}> = ({ title, subtitle }) => (
	<Box sx={{ mb: 1.25 }}>
		<Typography variant="subtitle1" fontWeight={700}>
			{title}
		</Typography>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block" }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog wrapper                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditCourseSession: FC<EditCourseSessionProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="edit-training-session-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="edit-training-session-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.EditCourseSession.L169C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.EditTrainingSession.Title">
						Edit training session
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block", pr: 5 }}
				>
					<Translate String="Training.EditTrainingSession.Subtitle">
						Update schedule, expiry, trainer, participants, and notes.
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelRight.TrainingSession.EditCourseSession.L191C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<EditTrainingSessionForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                         */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditTrainingSessionForm: FC<EditCourseSessionProps> = ({
	handleClose,
	session,
	course,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const { timezone } = useTime();
	const navigate = useNavigate();
	const [groupToAdd, setGroupToAdd] = useState<number | null>(null);

	const { isLoading: isLoadingUsers, data: userOptions } =
		useFetchUserSummariesByCurrentCompany();
	const { isLoading: isLoadingGroups, data: groupOptions } = useFetchGroups();
	const { data: groupMembersData } = useFetchUserGroupMembershipStatus(
		groupToAdd ?? 0,
		{ enabled: groupToAdd != null && groupToAdd > 0 },
	);

	const users = Array.isArray(userOptions) ? userOptions : [];
	const groups = Array.isArray(groupOptions) ? groupOptions : [];

	const updateTrainingSession = useMutateUpdateTrainingSession();
	const deleteTrainingSession = useMutateDeleteTrainingSession();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isArchived = session.Status === TrainingSessionStatus.Archived;
	const sessionWithGroup = session as SessionWithOptionalGroup;
	const sessionExpiresDate = (session as { ExpiresDate?: Date | string | null })
		.ExpiresDate;

	const initialValues = useMemo(
		() => ({
			CourseId: course.Id,
			Information: session.Information ?? "",
			SessionDate: toDateTimeLocalValue(
				typeof session.SessionDate === "string"
					? session.SessionDate
					: (session.SessionDate?.toISOString?.() ?? ""),
				timezone,
			),
			ExpiresDate: getInitialExpiresDate(
				course.Duration,
				sessionExpiresDate != null
					? typeof sessionExpiresDate === "string"
						? sessionExpiresDate
						: (sessionExpiresDate?.toISOString?.() ?? null)
					: null,
				timezone,
			),
			DisableExpiresDate: getInitialDisableExpiresDate(course.Duration),
			TrainerId: session.Trainer?.Id ?? "",
			StudentIds: (session.Students ?? []).map((s) => s.Id),
			GroupId: getSessionGroupId(sessionWithGroup),
			AddedGroupIds: (() => {
				const groupId = getSessionGroupId(sessionWithGroup);
				return groupId != null ? [groupId] : [];
			})(),
			Duration: (session.Duration ?? "").replace(/([A-Z])/g, " $1").trim(),
		}),
		[
			course.Id,
			course.Duration,
			session,
			sessionExpiresDate,
			sessionWithGroup,
			timezone,
		],
	);

	const validationSchema = useMemo(
		() => EditCourseSessionValidation(users),
		[users],
	);
	const [isDeleting, setIsDeleting] = useState(false);

	// Group add state - will be consumed by useEffect when Formik is ready
	const [pendingGroupAdd, setPendingGroupAdd] = useState<{
		groupId: number;
		setFieldValue: (f: string, v: unknown) => Promise<unknown> | void;
		currentIds: number[];
	} | null>(null);

	// When group members load, add them to StudentIds and show confirmation
	useEffect(() => {
		if (!pendingGroupAdd || !groupMembersData?.Items) return;
		const { groupId, setFieldValue, currentIds } = pendingGroupAdd;
		const members = groupMembersData.Items;
		const memberIds = members
			.filter(
				(member: GroupEmployee) =>
					member.GroupMembershipType?.IsMember !== false,
			)
			.map((member: GroupEmployee) => member.Id);
		const merged = Array.from(new Set([...currentIds, ...memberIds]));
		void setFieldValue("StudentIds", merged);
		void setFieldValue("GroupId", groupId); // also set form GroupId for API
		const groupName = groups.find((g) => g.Id === groupId)?.Name ?? "group";
		void AlertToast.success(
			`${memberIds.length} members added from ${groupName}`,
		);
		setPendingGroupAdd(null);
		setGroupToAdd(null);
	}, [pendingGroupAdd, groupMembersData, groups]);

	if (isLoadingUsers || isLoadingGroups) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const handleSubmit = async (values: FormValues) => {
		try {
			const req: TrainingSessionRequest = {
				CourseId: values.CourseId,
				Information: values.Information,
				SessionDate: fromDateTimeLocalValue(values.SessionDate, timezone),
				ExpiresDate: getRequestExpiresDate(
					course.Duration,
					values.ExpiresDate
						? fromDateTimeLocalValue(values.ExpiresDate, timezone)
						: null,
					values.DisableExpiresDate,
				),
				TrainerId: asNumberOrNull(values.TrainerId) ?? 0,
				StudentIds: Array.isArray(values.StudentIds)
					? values.StudentIds.map((value) => Number(value))
					: [],
				GroupId:
					values.GroupId == null || values.GroupId === ""
						? null
						: Number(values.GroupId),
			};

			await updateTrainingSession({ sessionId: session.Id, request: req });
			handleClose();
			void AlertToast.success();
			navigate(`/training/courses/${course.Id}/sessions/${session.Id}`);
		} catch (error) {
			console.error(error);
			handleClose();
			void AlertToast.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			handleClose();
			const confirmation = await AlertRequest.delete();
			if (!confirmation.isConfirmed) return;
			await deleteTrainingSession(session.Id);
			AlertRequest.deleteConfirmed(`${course.Name} training session`);
			navigate(`/training/courses/${course.Id}`);
		} catch (error) {
			console.error(error);
			void AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<Formik<FormValues>
			key={String(session.Id)}
			initialValues={initialValues}
			enableReinitialize
			onSubmit={handleSubmit}
			validationSchema={validationSchema}
		>
			{({ values, setFieldValue, isSubmitting }) => {
				const sectionCardSx = {
					...sharedTaskDialogPanelSx(theme),
					p: { xs: 2, md: 3 },
				};
				const selectedIds = Array.isArray(values.StudentIds)
					? values.StudentIds.map(Number).filter(Boolean)
					: [];
				const selectedUsers = users.filter((u) => selectedIds.includes(u.Id));
				const selectedGroupIds = Array.isArray(values.AddedGroupIds)
					? values.AddedGroupIds.map(Number).filter(Boolean)
					: [];
				const selectedGroups = groups.filter((group) =>
					selectedGroupIds.includes(group.Id),
				);

				return (
					<Form>
						<Stack spacing={3}>
							{/* 1. Session Details */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									title={
										<Translate String="Training.EditTrainingSession.Section.Details">
											Session Details
										</Translate>
									}
									subtitle={
										<Translate String="Training.EditTrainingSession.Section.Details.Subtitle">
											Date, validity and responsible trainer
										</Translate>
									}
								/>

								<Stack spacing={2}>
									<CustomInput
										id="SessionDate"
										type="datetime-local"
										label={GetLanguageValueAsString(
											translation,
											"Training.Session.Date",
											"Date",
										)}
										name="SessionDate"
										translationString="Training.EditTrainingSession.Date"
										disabled={isArchived}
										style={{ width: "100%" }}
									/>

									<CustomInput
										id="Duration"
										type="text"
										label={GetLanguageValueAsString(
											translation,
											"Training.EditTrainingSession.Duration",
											"Course Duration",
										)}
										name="Duration"
										translationString="Training.EditTrainingSession.Duration"
										readOnly
										style={{ width: "100%" }}
									/>

									{/* Validity: Expiry + Disable */}
									<Stack
										direction={{ xs: "column", sm: "row" }}
										spacing={2}
										alignItems={{ xs: "flex-start", sm: "center" }}
									>
										<Box sx={{ flex: 1, width: "100%" }}>
											<Field name="ExpiresDate">
												{({
													field,
													meta,
												}: FieldProps<string | null, FormValues>) => (
													<TextField
														{...field}
														id="ExpiresDate"
														type="datetime-local"
														label={
															<Translate String="Training.EditTrainingSession.ExpiryDate">
																Validity
															</Translate>
														}
														fullWidth
														size="small"
														InputProps={{
															readOnly: values.DisableExpiresDate,
														}}
														InputLabelProps={{ shrink: true }}
														disabled={isArchived}
														error={meta.touched && Boolean(meta.error)}
														helperText={meta.touched && meta.error}
														sx={{
															"& .MuiOutlinedInput-root": {
																borderRadius: 2,
																bgcolor: "background.paper",
															},
														}}
													/>
												)}
											</Field>
										</Box>
										<Box sx={{ display: "inline-flex", alignItems: "center" }}>
											<CustomCheckbox
												id="DisableExpiresDate"
												name="DisableExpiresDate"
												label={GetLanguageValueAsString(
													translation,
													"Training.EditTrainingSession.DisableHint",
													"Disable expiry",
												)}
												translationString="Training.EditTrainingSession.DisableHint"
												disabled={
													course.Duration !== TrainingCourseDuration.Custom ||
													isArchived
												}
												asSwitch
											/>
										</Box>
									</Stack>

									<Field name="TrainerId">
										{({
											field,
											meta,
											form,
										}: FieldProps<number | string, FormValues>) => (
											<FormControl
												fullWidth
												size="small"
												error={meta.touched && Boolean(meta.error)}
												disabled={isArchived}
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: 2,
														bgcolor: "background.paper",
													},
												}}
											>
												<InputLabel id="edit-session-trainer-label" shrink>
													<Translate String="Training.EditTrainingSession.Trainer">
														Responsible Trainer
													</Translate>
												</InputLabel>
												<Select
													{...field}
													labelId="edit-session-trainer-label"
													label={
														<Translate String="Training.EditTrainingSession.Trainer">
															Responsible Trainer
														</Translate>
													}
													value={field.value ?? ""}
													onChange={(e) => {
														const v = e.target.value;
														void form.setFieldValue(
															"TrainerId",
															v === "" ? "" : Number(v),
														);
													}}
													displayEmpty
												>
													<MenuItem value="">
														<em>
															<Translate String="Training.EditTrainingSession.SelectTrainer">
																Select trainer...
															</Translate>
														</em>
													</MenuItem>
													{users.map((u) => (
														<MenuItem key={u.Id} value={u.Id}>
															{u.Name}
														</MenuItem>
													))}
												</Select>
												{meta.touched && meta.error && (
													<FormHelperText>{meta.error}</FormHelperText>
												)}
											</FormControl>
										)}
									</Field>
								</Stack>
							</Paper>

							{/* 2. Participants */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									title={
										<>
											<Translate String="Training.EditTrainingSession.Section.Participants">
												Participants
											</Translate>
											{selectedIds.length > 0 && (
													<Typography
														component="span"
														variant="body2"
														color="primary"
														sx={{ ml: 1, fontWeight: 600 }}
													>
														({selectedIds.length}{" "}
														<Translate String="Common.Selected">selected</Translate>)
													</Typography>
												)}
										</>
									}
									subtitle={
										<Translate String="Training.EditTrainingSession.Section.Participants.Subtitle">
											Search and select participants, or add by group
										</Translate>
									}
								/>

								{/* Bulk actions */}
								<Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
									<ProcedureButton
										size="small"
										variant="outlined"
										onClick={() => {
											void setFieldValue(
												"StudentIds",
												users.map((u) => u.Id),
											);
										}}
										disabled={isArchived}
										sx={{ whiteSpace: "nowrap" }}
									>
										<Translate String="Common.SelectAll">Select all</Translate>
									</ProcedureButton>
									<ProcedureButton
										size="small"
										variant="outlined"
										intent="warning"
										onClick={() => {
											void setFieldValue("StudentIds", []);
											void setFieldValue("AddedGroupIds", []);
											void setFieldValue("GroupId", null);
											setGroupToAdd(null);
											setPendingGroupAdd(null);
										}}
										disabled={isArchived}
										sx={{ whiteSpace: "nowrap" }}
									>
										<Translate String="Common.ClearAll">Clear all</Translate>
									</ProcedureButton>
								</Stack>

								{/* Searchable multi-select */}
								<Autocomplete
									multiple
									options={users}
									getOptionLabel={(opt: UserSummary) => opt.Name ?? ""}
									value={selectedUsers}
									disableCloseOnSelect
									onChange={(_, newVal) => {
										void setFieldValue(
											"StudentIds",
											newVal.map((u) => u.Id),
										);
									}}
									disabled={isArchived}
									filterSelectedOptions
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder={GetLanguageValueAsString(
												translation,
												"Training.EditTrainingSession.SearchParticipants",
												"Search participants...",
											)}
											size="small"
											sx={{
												"& .MuiOutlinedInput-root": {
													bgcolor: "background.paper",
													"& fieldset": { borderRadius: 1 },
												},
											}}
										/>
									)}
									renderOption={(props, option, { selected }) => (
										<li
											{...props}
											key={option.Id}
											style={{
												borderBottom: "1px solid",
												borderColor: "divider",
											}}
										>
											<Checkbox
												checked={selected}
												size="small"
												sx={{ mr: 1 }}
											/>
											{option.Name}
										</li>
									)}
								/>

								{/* Add by group */}
								<Box sx={{ mt: 2 }}>
									<Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
										<Translate String="Training.EditTrainingSession.AddByGroup">
											Add by group
										</Translate>
										{selectedGroupIds.length > 0 && (
											<Typography
												component="span"
												variant="body2"
												color="primary"
												sx={{ ml: 1, fontWeight: 600 }}
											>
												({selectedGroupIds.length}{" "}
												<Translate String="Common.Selected">selected</Translate>)
											</Typography>
										)}
									</Typography>
									<Autocomplete
										multiple
										options={groups}
										getOptionLabel={(option) => option.Name ?? ""}
										value={selectedGroups}
										disabled={isArchived}
										filterSelectedOptions
										disableClearable
										sx={{
											"& .MuiOutlinedInput-root": {
												height: "auto",
												minHeight: 40,
												alignItems: "flex-start",
												flexWrap: "wrap",
												bgcolor: "background.paper",
												borderRadius: 2,
												py: 0.5,
											},
										}}
										onChange={(_, newValue, reason) => {
											const nextSelectedGroupIds = newValue
												.map((group) => group.Id)
												.filter(
													(groupId): groupId is number =>
														typeof groupId === "number",
												);
											const newlyAddedGroup = newValue.find(
												(group) => !selectedGroupIds.includes(group.Id),
											);

											void setFieldValue(
												"AddedGroupIds",
												nextSelectedGroupIds,
											);

											if (
												reason === "removeOption" ||
												reason === "clear"
											) {
												void setFieldValue(
													"GroupId",
													nextSelectedGroupIds.at(-1) ?? null,
												);
												if (nextSelectedGroupIds.length === 0) {
													setGroupToAdd(null);
													setPendingGroupAdd(null);
												}
												return;
											}

											if (!newlyAddedGroup) {
												return;
											}

											void setFieldValue("GroupId", newlyAddedGroup.Id);
											setGroupToAdd(newlyAddedGroup.Id);
											setPendingGroupAdd({
												groupId: newlyAddedGroup.Id,
												setFieldValue,
												currentIds: selectedIds,
											});
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={
													<Translate String="Training.EditTrainingSession.AddGroup">
														Group
													</Translate>
												}
												size="small"
											/>
										)}
										renderOption={(renderProps, option, { selected }) => (
											<li
												{...renderProps}
												key={option.Id}
												style={{
													borderBottom: "1px solid",
													borderColor: "divider",
												}}
											>
												<Checkbox
													checked={selected}
													size="small"
													sx={{ mr: 1 }}
												/>
												{option.Name}
											</li>
										)}
									/>
									{selectedGroups.length > 0 && (
										<Stack
											direction="row"
											flexWrap="wrap"
											gap={1}
											sx={{ mt: 1 }}
										>
											{selectedGroups.map((group) => (
												<Chip
													key={group.Id}
													label={group.Name ?? ""}
													size="small"
												/>
											))}
										</Stack>
									)}
								</Box>
							</Paper>

							{/* 3. Notes */}
							<Paper variant="outlined" sx={sectionCardSx}>
								<SectionHeader
									title={
										<Translate String="Common.Notes">Notes</Translate>
									}
									subtitle={
										<Translate String="Training.EditTrainingSession.NotesSubtitle">
											Additional information about this session
										</Translate>
									}
								/>
								<SATOSEditorField
									id="Information"
									name="Information"
									label={GetLanguageValueAsString(
										translation,
										"Training.Session.Info",
										"Information",
									)}
									disabled={isArchived}
									aria-required={true}
									style={{ minWidth: "100%" }}
									imageUploadDisabled={true}
								/>
								{session.Comment && (
									<Box
										sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
									>
										<Typography
											variant="subtitle2"
											fontWeight={600}
											sx={{ mb: 0.5 }}
										>
											<Translate String="Common.Comment">Comment</Translate>
										</Typography>
										<Typography variant="body2">
											{session.Comment || (
												<Translate String="Training.EditTrainingSession.NoComment">
													No comment.
												</Translate>
											)}
										</Typography>
									</Box>
								)}
							</Paper>
						</Stack>

						{/* Sticky footer */}
						<DialogActions
							sx={{
								...sharedTaskDialogActionsSx(theme, isMobile),
								px: 0,
								pt: 2,
								mt: 3,
								flexWrap: "wrap",
								justifyContent: isMobile ? "center" : "flex-end",
								gap: 1.5,
								display: "flex",
							}}
						>
							<CustomDeleteButton
								handleDelete={handleDelete}
								loading={isDeleting}
								disabled={isArchived || isSubmitting || isDeleting}
								textOverride={
									<Translate String="Training.EditTrainingSession.DeleteButton">
										Delete Session
									</Translate>
								}
							/>
							<CancelActionButton
								onClick={handleClose}
								disabled={isSubmitting || isDeleting}
							>
								<Translate String="Procedure.Button.Cancel">Cancel</Translate>
							</CancelActionButton>
							<SaveActionButton
								type="submit"
								loading={isSubmitting}
								loadingText={
									<Translate String="LabDaily.Saving">Saving...</Translate>
								}
								disabled={isArchived || isSubmitting || isDeleting}
							>
								<Translate String="Training.EditTrainingSession.SaveChanges">
									Save changes
								</Translate>
							</SaveActionButton>
						</DialogActions>
					</Form>
				);
			}}
		</Formik>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                     */
/* ──────────────────────────────────────────────────────────────────────────── */

function getInitialExpiresDate(
	duration: TrainingCourseDuration,
	expiresDate: string | Date | null,
	tz: string,
): string | null {
	if (duration === TrainingCourseDuration.Custom && expiresDate === null)
		return null;
	if (!expiresDate) return null;
	const str =
		typeof expiresDate === "string"
			? expiresDate
			: expiresDate instanceof Date
				? expiresDate.toISOString?.()
				: null;
	return str ? toDateTimeLocalValue(str, tz) : null;
}

function getInitialDisableExpiresDate(
	duration: TrainingCourseDuration,
): boolean {
	// When Custom: date is enabled by default so user can set it; check "Disable" for no expiry
	if (duration === TrainingCourseDuration.Custom) return false;
	// When not Custom: expiry is fixed by course duration, date stays disabled
	return true;
}

function getRequestExpiresDate(
	courseDuration: TrainingCourseDuration,
	expiresDate: Date | null,
	disableExpiresDate: boolean,
): Date | null {
	if (courseDuration !== TrainingCourseDuration.Custom || disableExpiresDate)
		return null;
	return expiresDate;
}

function asNumberOrNull(v: unknown): number | null {
	if (v === "" || v === null || typeof v === "undefined") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

function getSessionGroupId(session: SessionWithOptionalGroup): number | null {
	if (typeof session.GroupId === "number") return session.GroupId;
	const groupId = session.Group?.Id;
	return typeof groupId === "number" ? groupId : null;
}
